import React, { type ComponentPropsWithoutRef } from 'react';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { Menu } from '../../common/Menu';

type BudgetMenuProps = Omit<
  ComponentPropsWithoutRef<typeof Menu>,
  'onMenuSelect' | 'items'
> & {
  onCopyLastMonthAverage: () => void;
  onSetMonthsAverage: (numberOfMonths: number) => void;
  onApplyBudgetTemplate?: () => void;
  onApplyPreviousYearActual?: () => void;
  onApplyPreviousYearBudget?: () => void;
  onResetToZero?: () => void;
};
export function BudgetMenu({
  onCopyLastMonthAverage,
  onSetMonthsAverage,
  onApplyBudgetTemplate,
  onApplyPreviousYearActual,
  onApplyPreviousYearBudget,
  onResetToZero,
  ...props
}: BudgetMenuProps) {
  const isGoalTemplatesEnabled = useFeatureFlag('goalTemplatesEnabled');
  const onMenuSelect = (name: string) => {
    switch (name) {
      case 'copy-single-last':
        onCopyLastMonthAverage?.();
        break;
      case 'set-single-3-avg':
        onSetMonthsAverage?.(3);
        break;
      case 'set-single-6-avg':
        onSetMonthsAverage?.(6);
        break;
      case 'set-single-12-avg':
        onSetMonthsAverage?.(12);
        break;
      case 'apply-single-category-template':
        onApplyBudgetTemplate?.();
        break;
      case 'apply-previous-year-actual':
        onApplyPreviousYearActual?.();
        break;
      case 'apply-previous-year-budget':
        onApplyPreviousYearBudget?.();
        break;
      case 'reset-category-to-zero':
        onResetToZero?.();
        break;
      default:
        throw new Error(`Unrecognized menu item: ${name}`);
    }
  };

  return (
    <Menu
      {...props}
      onMenuSelect={onMenuSelect}
      items={[
        {
          name: 'copy-single-last',
          text: 'Copy last month’s budget',
        },
        {
          name: 'set-single-3-avg',
          text: 'Set to 3 month average',
        },
        {
          name: 'set-single-6-avg',
          text: 'Set to 6 month average',
        },
        {
          name: 'set-single-12-avg',
          text: 'Set to yearly average',
        },
        ...(onApplyPreviousYearActual
          ? [
              {
                name: 'apply-previous-year-actual',
                text: 'Apply previous year actual',
              },
            ]
          : []),
        ...(onApplyPreviousYearBudget
          ? [
              {
                name: 'apply-previous-year-budget',
                text: 'Apply previous year budget',
              },
            ]
          : []),
        ...(onResetToZero
          ? [
              {
                name: 'reset-category-to-zero',
                text: 'Reset to zero',
              },
            ]
          : []),
        ...(isGoalTemplatesEnabled && onApplyBudgetTemplate
          ? [
              {
                name: 'apply-single-category-template',
                text: 'Apply budget template',
              },
            ]
          : []),
      ]}
    />
  );
}
